import { Platform, ScrollView, StyleSheet } from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { ResearchCompany } from '../types';
import { SearchResultCard } from './SearchResultCard';

interface SearchResultsPanelProps {
  readonly companies: ResearchCompany[];
  readonly totalResults: number;
  readonly cacheStatus?: 'hit' | 'miss' | 'unavailable';
}

export function SearchResultsPanel({
  companies,
  totalResults,
  cacheStatus,
}: SearchResultsPanelProps) {
  if (companies.length === 0) return null;

  const cacheLabel = getCacheLabel(cacheStatus);

  return (
    <Box flex={1} borderTopWidth={1} borderTopColor={colors.border.subtle}>
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        paddingX="lg"
        paddingY="md"
      >
        <Text variant="bodySmall" color={colors.text.secondary} style={styles.title}>
          Prospect ranking
        </Text>
        <Box paddingX="sm" paddingY="xxs" borderRadius="md" backgroundColor={colors.primary.muted}>
          <Text variant="caption" color={colors.primary.neon} style={styles.countText}>
            {totalResults}
          </Text>
        </Box>
      </Box>
      {cacheLabel && (
        <Text variant="caption" color={colors.text.tertiary} style={styles.cacheLabel}>
          {cacheLabel}
        </Text>
      )}

      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
        keyboardShouldPersistTaps="handled"
      >
        {companies.map((company, index) => (
          <SearchResultCard
            key={`${company.name}-${company.website ?? company.source}-${index}`}
            company={company}
            index={index}
          />
        ))}
      </ScrollView>
    </Box>
  );
}

const styles = StyleSheet.create({
  title: {
    fontWeight: '600',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    fontSize: 11,
  },
  cacheLabel: {
    paddingHorizontal: tokens.spacing.lg,
    paddingBottom: tokens.spacing.sm,
  },
  countText: {
    fontSize: 11,
    fontWeight: '600',
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: tokens.spacing.lg,
    paddingBottom: tokens.spacing.xl,
    gap: 8,
  },
});

function getCacheLabel(cacheStatus: SearchResultsPanelProps['cacheStatus']) {
  if (cacheStatus === 'hit') return 'Using recent organization research';
  if (cacheStatus === 'miss') return 'No recent cache match; agents searched live sources';
  if (cacheStatus === 'unavailable') return 'Research cache temporarily unavailable';
  return undefined;
}
